/**
 * The Projektpartner page (rendered at `/developers`), built to the website
 * draft's "3f" screen, and the Über uns page ("3g").
 *
 * Presentation only — the property-submission handler stays in
 * `pages/Owners.tsx`, since it invokes a real edge function.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { ChevronDown, ArrowRight, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/marketing/whatsapp-icon";
import { SmoothImage } from "@/components/ui/smooth-image";
import { CountUpStat } from "@/components/marketing/count-up-stat";
import {
  INK,
  INK_DEEP,
  MUTED,
  MUTED_SOFT,
  GREY,
  SAND,
  LINE,
  LINE_SOFT,
  LINE_STRONG,
  IMAGE_BG,
  EASE,
} from "@/lib/marketing-theme";
import {
  PARTNER_STATS,
  DEV_WHY,
  PARTNER_STEPS,
  PARTNER_FORM,
  DEV_FAQ,
  ABOUT_STORY,
  ABOUT_VALUES,
  FOUNDERS,
  type Bilingual,
} from "./partner-data";

type Lang = { de: boolean };
const pick = (v: Bilingual, de: boolean) => (de ? v.de : v.en);

const H2 =
  "font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]";
const H2_STYLE = { letterSpacing: "-0.04em", color: INK } as const;

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: delay * 0.08, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Headline reveal: each word rides up out of its own clipping box, so the line
 * assembles rather than fading in as one block.
 *
 * The viewport trigger sits on the HEADING, not on each word. When every word
 * carried its own `whileInView` the words never appeared: a word is translated
 * 108% down and clipped by its wrapper, so its own intersection ratio cannot
 * reach the `amount` threshold, and `once: true` meant one miss hid it for
 * good. "Warum mit uns partnern." shipped to dev as an empty column that way.
 * The heading is an ordinary block element and observes reliably; the words
 * follow it through variants. Words stay real text nodes —
 * the space between them is outside the animated span so copy stays selectable
 * and screen readers still hear one sentence.
 */
function WordReveal({
  text,
  className,
  style,
  delay = 0,
  as: Tag = "h2",
  /** Headlines above the fold play on mount; section headings wait for scroll. */
  trigger = "inView",
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
  as?: "h1" | "h2";
  trigger?: "mount" | "inView";
}) {
  const reduce = useReducedMotion();
  const words = text.split(" ");
  const MotionTag = Tag === "h1" ? motion.h1 : motion.h2;

  // Reduced motion gets the plain heading — no hidden state to get stuck in.
  if (reduce) {
    return (
      <Tag className={className} style={style}>
        {text}
      </Tag>
    );
  }

  return (
    <MotionTag
      className={className}
      style={style}
      initial="hidden"
      variants={{
        hidden: {},
        shown: { transition: { delayChildren: delay, staggerChildren: 0.045 } },
      }}
      {...(trigger === "mount"
        ? { animate: "shown" }
        : {
            whileInView: "shown" as const,
            viewport: { once: true, amount: 0.2 },
          })}
    >
      {words.map((word, i) => (
        <span key={`${word}-${i}`}>
          {/* The mask clips to its padding box, so a bare overflow-hidden cuts
              the descenders off g/j/p/y. The padding gives them room and the
              matching negative margin keeps the line box the same height. */}
          <span className="inline-block overflow-hidden pb-[0.18em] -mb-[0.18em] align-bottom">
            <motion.span
              className="inline-block"
              variants={{ hidden: { y: "108%" }, shown: { y: "0%" } }}
              transition={{ duration: 0.75, ease: EASE }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : null}
        </span>
      ))}
    </MotionTag>
  );
}

/** A hairline that draws itself left-to-right as the row enters. */
function DrawnRule({ delay = 0 }: { delay?: number }) {
  return (
    <motion.span
      aria-hidden="true"
      className="block h-px w-full origin-left"
      style={{ background: LINE_STRONG }}
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 0.8 }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* 3f — Projektpartner                                                 */
/* ------------------------------------------------------------------ */

export function PartnerHero({
  de,
  onPropose,
  onContact,
}: Lang & { onPropose: () => void; onContact: () => void }) {
  // The hero photo drifts a little slower than the page, which keeps the
  // headline and the stat card feeling layered over it rather than glued on.
  const frameRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: frameRef,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <section className="bg-white">
      <div
        className="mx-auto max-w-[1328px] px-5 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 3rem)" }}
      >
        <WordReveal
          as="h1"
          trigger="mount"
          text={
            de
              ? "Lassen Sie uns gemeinsam ein Projekt starten."
              : "Let's start a project together."
          }
          className="max-w-[1000px] font-display text-[2.5rem] font-semibold leading-[1.03] sm:text-[3.25rem] lg:text-[3.625rem]"
          style={{ letterSpacing: "-0.042em", color: INK }}
        />

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
          className="mt-8 flex flex-col items-start justify-between gap-7 pb-12 lg:flex-row lg:items-center"
        >
          <p
            className="max-w-[520px] text-[17px] leading-[1.6]"
            style={{ color: MUTED }}
          >
            {de
              ? "Wir transformieren, möblieren und betreiben — Sie erhalten verlässliche Mieteinnahmen."
              : "We convert, furnish and operate — you receive reliable rental income."}
          </p>
          <div className="flex flex-wrap items-center gap-5">
            <button
              type="button"
              onClick={onPropose}
              className="inline-flex h-[54px] items-center rounded-full px-8 text-[16px] font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
              style={{ background: INK }}
            >
              {de ? "Immobilie vorschlagen" : "Propose a property"}
            </button>
            <button
              type="button"
              onClick={onContact}
              className="group inline-flex items-center gap-2.5 text-[16px] font-semibold"
              style={{ color: INK }}
            >
              {de ? "Kontakt aufnehmen" : "Get in touch"}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </button>
          </div>
        </motion.div>
      </div>

      <div className="px-5 pb-16 sm:px-8 lg:px-14">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="relative mx-auto max-w-[1328px]"
        >
          <div
            ref={frameRef}
            className="relative h-[280px] overflow-hidden rounded-[14px] sm:h-[420px] lg:h-[520px]"
            style={{ background: IMAGE_BG }}
          >
            <motion.div
              style={{ y: photoY }}
              className="absolute -inset-y-[8%] inset-x-0"
            >
              <SmoothImage
                src="/assets/projektpartner-muenchen.png"
                alt=""
                wrapperClassName="absolute inset-0 h-full w-full"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="relative -mt-10 ml-0 flex w-fit max-w-full overflow-hidden rounded-[14px] bg-white shadow-[0_24px_48px_rgba(9,46,44,0.14)] sm:ml-8 lg:ml-10"
          >
            {PARTNER_STATS.map((s, i) => (
              <motion.div
                key={s.value}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: EASE,
                  delay: 0.6 + i * 0.12,
                }}
                className="flex flex-col gap-1 px-7 py-6 sm:px-8"
                style={{
                  borderLeft: i > 0 ? `1px solid ${LINE_SOFT}` : undefined,
                }}
              >
                <span
                  className="text-[26px] font-semibold leading-none"
                  style={{ letterSpacing: "-0.04em", color: INK }}
                >
                  <CountUpStat value={s.value} de={de} immediate />
                </span>
                <span className="text-[14px]" style={{ color: MUTED }}>
                  {pick(s.label, de)}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export function PartnerWhy({ de }: Lang) {
  return (
    <section className="bg-white px-5 py-20 sm:px-8 lg:px-14">
      <div className="mx-auto max-w-[1328px]">
        <div className="grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <WordReveal
            text={de ? "Warum mit uns partnern." : "Why partner with us."}
            className={H2}
            style={H2_STYLE}
          />
          <div className="grid gap-x-12 gap-y-8 sm:grid-cols-2">
            {DEV_WHY.map((item, i) => (
              <Reveal key={item.name.en} delay={i}>
                <div className="flex flex-col gap-2">
                  <DrawnRule delay={i * 0.08} />
                  <span
                    className="pt-6 text-[17px] font-semibold"
                    style={{ color: INK }}
                  >
                    {pick(item.name, de)}
                  </span>
                  <span
                    className="text-[15.5px] leading-[1.6]"
                    style={{ color: MUTED }}
                  >
                    {pick(item.body, de)}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* before / after — the draft leaves 96px between the benefit grid and
            these, and 64px read as cramped against the two-line bodies above. */}
        <div className="mt-24 grid gap-4 lg:grid-cols-2">
          {[
            {
              label: de ? "Vorher" : "Before",
              image: "/assets/dev-vorher-room.jpg",
              dark: false,
            },
            {
              label: de ? "Nachher" : "After",
              image: "/assets/dev-nachher.png",
              dark: true,
            },
          ].map((side, i) => (
            <motion.div
              key={side.label}
              initial={{ opacity: 0, x: i === 0 ? -36 : 36 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
            >
              <div
                className="group/ba relative h-[300px] overflow-hidden rounded-[14px] sm:h-[420px]"
                style={{
                  background: IMAGE_BG,
                }}
              >
                <SmoothImage
                  src={side.image}
                  alt={side.label}
                  wrapperClassName="absolute inset-0 h-full w-full"
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover/ba:scale-[1.04]"
                />
                <motion.span
                  initial={{ opacity: 0, y: -10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease: EASE, delay: 0.35 }}
                  className="absolute left-6 top-6 inline-flex h-[34px] items-center rounded-full px-4 text-[14px] font-semibold"
                  style={
                    side.dark
                      ? { background: INK, color: "#FFFFFF" }
                      : { background: "#FFFFFF", color: INK }
                  }
                >
                  {side.label}
                </motion.span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PartnerProcess({ de }: Lang) {
  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
    >
      <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
        <WordReveal
          text={
            de ? "So läuft die Partnerschaft." : "How the partnership runs."
          }
          className={H2}
          style={H2_STYLE}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PARTNER_STEPS.map((step, i) => (
            <Reveal key={step.no} delay={i}>
              <motion.article
                whileHover={{ y: -5 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="flex h-full flex-col gap-2 rounded-[14px] border bg-white p-7"
                style={{ borderColor: LINE }}
              >
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{
                    type: "spring",
                    stiffness: 340,
                    damping: 22,
                    delay: 0.1 + i * 0.05,
                  }}
                  className="mb-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold text-white"
                  style={{ background: INK }}
                >
                  {step.no}
                </motion.span>
                <span
                  className="text-[17px] font-semibold"
                  style={{ letterSpacing: "-0.02em", color: INK }}
                >
                  {pick(step.name, de)}
                </span>
                <span
                  className="text-[15px] leading-[1.55]"
                  style={{ color: MUTED }}
                >
                  {pick(step.body, de)}
                </span>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PartnerForm({
  de,
  onSubmit,
  submitting,
  formId,
}: Lang & {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  submitting: boolean;
  formId: string;
}) {
  return (
    <section className="bg-white px-5 py-24 sm:px-8 lg:px-14" id={formId}>
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <div className="flex flex-col gap-3">
          <WordReveal
            text={de ? "Immobilie vorschlagen." : "Propose a property."}
            className={H2}
            style={H2_STYLE}
          />
          <Reveal delay={2}>
            <p className="text-[16px]" style={{ color: MUTED }}>
              {de
                ? "Rückmeldung innerhalb von 24 Stunden."
                : "We reply within 24 hours."}
            </p>
          </Reveal>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-7">
          <div className="grid gap-x-10 gap-y-7 sm:grid-cols-2">
            {PARTNER_FORM.map((f, i) => (
              <motion.label
                key={f.field}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, ease: EASE, delay: i * 0.06 }}
                className="group/f flex flex-col gap-2"
              >
                <span
                  className="text-[14px] font-medium"
                  style={{ color: MUTED_SOFT }}
                >
                  {pick(f.label, de)}
                </span>
                <span className="relative block">
                  {f.options ? (
                    <select
                      name={f.field}
                      required
                      defaultValue=""
                      className="w-full border-0 border-b bg-transparent pb-2.5 text-[17px] outline-none"
                      style={{ borderColor: LINE_STRONG, color: INK }}
                    >
                      <option value="" disabled>
                        {pick(f.hint, de)}
                      </option>
                      {f.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {pick(o.label, de)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      name={f.field}
                      type={f.type ?? "text"}
                      min={f.type === "number" ? 1 : undefined}
                      required
                      placeholder={pick(f.hint, de)}
                      className="w-full border-0 border-b bg-transparent pb-2.5 text-[17px] outline-none placeholder:opacity-60"
                      style={{ borderColor: LINE_STRONG, color: INK }}
                    />
                  )}
                  {/* The underline grows in from the left on focus, instead of
                      the border simply thickening. */}
                  <span
                    aria-hidden="true"
                    className="absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-focus-within/f:scale-x-100"
                    style={{ background: INK }}
                  />
                </span>
              </motion.label>
            ))}
          </div>
          <motion.label
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.55, ease: EASE, delay: 0.36 }}
            className="group/f flex flex-col gap-2"
          >
            <span
              className="text-[14px] font-medium"
              style={{ color: MUTED_SOFT }}
            >
              {de ? "Zusätzliche Informationen" : "Anything else"}
            </span>
            <span className="relative block">
              <textarea
                name="message"
                rows={3}
                placeholder={
                  de
                    ? "Erzählen Sie uns mehr über Ihre Immobilie…"
                    : "Tell us more about your property…"
                }
                className="w-full resize-none border-0 border-b bg-transparent pb-2.5 text-[17px] outline-none placeholder:opacity-60"
                style={{ borderColor: LINE_STRONG, color: INK }}
              />
              <span
                aria-hidden="true"
                className="absolute bottom-[5px] left-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-300 ease-out group-focus-within/f:scale-x-100"
                style={{ background: INK }}
              />
            </span>
          </motion.label>
          <motion.button
            type="submit"
            disabled={submitting}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.42 }}
            whileHover={submitting ? undefined : { scale: 1.03 }}
            whileTap={submitting ? undefined : { scale: 0.98 }}
            className="inline-flex h-[54px] w-fit items-center gap-2.5 rounded-full px-8 text-[16px] font-semibold text-white disabled:opacity-60"
            style={{ background: INK }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {de ? "Wird gesendet…" : "Sending…"}
              </>
            ) : (
              <>
                {de ? "Immobilie einreichen" : "Submit property"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </>
            )}
          </motion.button>
        </form>
      </div>
    </section>
  );
}

export function PartnerFaq({ de }: Lang) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
    >
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
        <WordReveal
          text={de ? "Häufige Fragen." : "Frequently asked."}
          className={H2}
          style={H2_STYLE}
        />
        <div className="flex flex-col">
          {DEV_FAQ.map((item, i) => {
            const on = open === i;
            return (
              <Reveal key={item.q.en} delay={Math.min(i, 4)}>
                <div>
                  <DrawnRule delay={Math.min(i, 4) * 0.06} />
                  <button
                    type="button"
                    onClick={() => setOpen(on ? null : i)}
                    aria-expanded={on}
                    className="group/q flex w-full items-center justify-between gap-5 py-6 text-left"
                  >
                    <span
                      className="text-[17px] font-semibold transition-transform duration-300 group-hover/q:translate-x-1 sm:text-[19px]"
                      style={{ letterSpacing: "-0.02em", color: INK }}
                    >
                      {pick(item.q, de)}
                    </span>
                    {/* The chevron sits in a ring that fills once the answer is
                        open, so the state is legible without reading the copy. */}
                    <motion.span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border"
                      animate={{
                        backgroundColor: on ? INK : "rgba(0,0,0,0)",
                        borderColor: on ? INK : LINE_STRONG,
                        rotate: on ? 180 : 0,
                      }}
                      transition={{ duration: 0.35, ease: EASE }}
                    >
                      <ChevronDown
                        className="h-4 w-4"
                        style={{ color: on ? "#FFFFFF" : INK }}
                        strokeWidth={2.2}
                      />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {on && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <motion.p
                          initial={{ y: 8 }}
                          animate={{ y: 0 }}
                          exit={{ y: 8 }}
                          transition={{ duration: 0.35, ease: EASE }}
                          className="max-w-[68ch] pb-6 text-[15.5px] leading-[1.65]"
                          style={{ color: MUTED }}
                        >
                          {pick(item.a, de)}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PartnerClosing({
  de,
  onPropose,
}: Lang & { onPropose: () => void }) {
  return (
    <section
      className="relative overflow-hidden px-5 pt-24 sm:px-8 lg:px-14"
      style={{ background: INK_DEEP }}
    >
      <div className="relative mx-auto max-w-[1328px]">
        <div className="flex flex-col gap-7 pb-14">
          <WordReveal
            text={de ? "Projekt in Planung?" : "A project in the pipeline?"}
            className="font-display text-[2rem] font-semibold leading-[1.06] text-white sm:text-[2.5rem]"
            style={{ letterSpacing: "-0.04em" }}
          />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.24 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              onClick={onPropose}
              className="inline-flex h-[54px] items-center rounded-full bg-white px-8 text-[16px] font-semibold transition-transform duration-300 hover:-translate-y-0.5"
              style={{ color: INK_DEEP }}
            >
              {de ? "Immobilie vorschlagen" : "Propose a property"}
            </button>
            <a
              href="https://wa.me/498954193354"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-[54px] items-center gap-2.5 rounded-full border-[1.5px] px-7 text-[16px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
              style={{ borderColor: "rgba(255,255,255,0.3)" }}
            >
              <WhatsAppIcon className="h-[18px] w-[18px]" />
              {de ? "Schreib uns" : "Write to us"}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 3g — Über uns                                                       */
/* ------------------------------------------------------------------ */

export function AboutPage({ de }: Lang) {
  // Same treatment as the Projektpartner hero: the founders photo drifts a
  // little slower than the page as you scroll past it.
  const portraitRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: portraitRef,
    offset: ["start end", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  return (
    <div className="min-h-screen bg-white">
      <section
        className="px-5 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 3rem)" }}
      >
        <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
          <div className="flex flex-col gap-3">
            <WordReveal
              as="h1"
              trigger="mount"
              text={de ? "Über uns." : "About us."}
              className="font-display text-[2.5rem] font-semibold leading-[1.03] sm:text-[3.25rem]"
              style={{ letterSpacing: "-0.042em", color: INK }}
            />
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
              className="text-[18px] leading-[1.4]"
              style={{ color: MUTED }}
            >
              {de
                ? "Warum wir coliviq gegründet haben."
                : "Why we founded coliviq."}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="flex flex-col gap-4"
          >
            {/* Same frame the other pages' heroes use, rather than the photo's
                own 1200x857 ratio — at full width that ratio made this the
                tallest block on the site. object-cover with a top-biased
                position keeps the founders' heads in frame at the shorter
                height, which is what the ratio was protecting against. */}
            <motion.div
              ref={portraitRef}
              className="relative h-[280px] w-full overflow-hidden rounded-[14px] sm:h-[420px] lg:h-[520px]"
              style={{ background: IMAGE_BG }}
              initial={{ clipPath: "inset(12% 0% 12% 0% round 14px)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0% round 14px)" }}
              transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
            >
              <motion.div
                style={{ y: portraitY }}
                className="absolute inset-0 h-full w-full"
              >
                <SmoothImage
                  src={FOUNDERS.image}
                  alt={FOUNDERS.names}
                  wrapperClassName="absolute inset-0 h-full w-full"
                  className="h-full w-full object-cover object-[50%_28%]"
                />
              </motion.div>
            </motion.div>
            <div className="flex flex-col gap-0.5">
              <span
                className="text-[17px] font-semibold"
                style={{ color: INK }}
              >
                {FOUNDERS.names}
              </span>
              <span className="text-[14.5px]" style={{ color: MUTED_SOFT }}>
                {pick(FOUNDERS.role, de)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <WordReveal
            text={de ? "Unsere Geschichte." : "Our story."}
            className={H2}
            style={H2_STYLE}
          />
          <div className="flex flex-col gap-6">
            {ABOUT_STORY.map((para, i) => (
              <Reveal key={i} delay={i}>
                <p
                  className="max-w-[700px] text-[17px] leading-[1.65] sm:text-[19px]"
                  style={{ color: i === 0 ? INK : MUTED }}
                >
                  {pick(para, de)}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-24 sm:px-8 lg:px-14">
        <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <WordReveal
            text={de ? "Woran wir glauben." : "What we believe."}
            className={H2}
            style={H2_STYLE}
          />
          <div className="flex flex-col">
            {ABOUT_VALUES.map((v, i) => (
              <Reveal key={v.name.en} delay={i}>
                <DrawnRule delay={i * 0.08} />
                <div className="grid gap-2 py-7 sm:grid-cols-[minmax(0,280px)_minmax(0,1fr)] sm:gap-10">
                  <span
                    className="text-[17px] font-semibold"
                    style={{ letterSpacing: "-0.02em", color: INK }}
                  >
                    {pick(v.name, de)}
                  </span>
                  <span
                    className="text-[15.5px] leading-[1.6]"
                    style={{ color: MUTED }}
                  >
                    {pick(v.body, de)}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Link to="/contact" className="sr-only" aria-hidden="true" tabIndex={-1}>
        {de ? "Kontakt" : "Contact"}
      </Link>
    </div>
  );
}
